import {
  v2_isSingleChoiceProperty,
  v2_ProductDetail,
  v2_selectMultipleChoiceConfigurationPropertyValue,
  v2_selectSingleChoiceConfigurationPropertyValue,
  v2_useProduct,
} from '@crocodille/common';
import { isSome, noop } from '@crocodille/util';
import { formatCurrency } from '@utils/format';
import cn from 'classnames';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import backgroundImage from '../../../../public/configurable-product/config-salad-background.jpg';
import plateImage from '../../../../public/configurable-product/plate.png';
import CartQuantity from '../../cart/cart-quantity/cart-quantity';
import Button from '../../elements/button/button';
import Image from '../../elements/image/image';
import ChevronDownIcon from '../../icons/chevron-down-icon';

import styles from './configurable-product.module.scss';

const IngredientElement: React.FC<{
  readonly name: string;
  readonly price?: number | null;
  readonly quantity: number;
  readonly disabled?: boolean;
  readonly disabledDecrement?: boolean;
  readonly disabledIncrement?: boolean;
  readonly max?: number;
  onChange?: (value: number) => void;
}> = ({
  name,
  price,
  quantity,
  disabled = false,
  disabledDecrement = false,
  disabledIncrement = false,
  max,
  onChange = noop,
}) => {
  const {
    i18n: { language },
  } = useTranslation();

  return (
    <div
      className={cn(styles.ingredient, {
        [styles.isDisabled]: disabled,
      })}
    >
      <span className={styles.ingredientLabel}>{name}</span>
      {isSome(price) ? (
        <span className={styles.ingredientPrice}>
          {formatCurrency(price, 'CZK', language)}
        </span>
      ) : null}
      <CartQuantity
        value={quantity}
        onChange={onChange}
        disabledDecrement={disabledDecrement}
        disabledIncrement={disabledIncrement}
        disabled={disabled}
        max={max}
      />
    </div>
  );
};

const IngredientGroupElement: React.FC<{
  readonly property: v2_ProductDetail['configuration'][number];
  readonly value: Partial<Record<string, number>>;
  readonly isOpen: boolean;
  onOpen?(): void;
  onSetIngredientValue?(ingredient: string, quantity: number): void;
}> = ({
  property,
  value,
  isOpen,
  onOpen = noop,
  onSetIngredientValue = noop,
}) => {
  const totalSelected = Object.values(value)
    .filter(isSome)
    .reduce((prev, curr) => prev + curr, 0);

  const groupFull =
    isSome(property.maxSelectedOptions) &&
    totalSelected >= property.maxSelectedOptions;
  const groupInvalid =
    isSome(property.minSelectedOptions) &&
    totalSelected < property.minSelectedOptions;

  return (
    <div
      className={cn(styles.group, {
        [styles.isInvalid]: groupInvalid,
        [styles.isOpen]: isOpen,
      })}
      data-invalid-configurator-group={groupInvalid ? 'true' : undefined}
    >
      <Button
        onClick={onOpen}
        symmetrical={false}
        display="block"
        className={styles.groupButton}
      >
        <span className={styles.groupName}>{property.name}</span>{' '}
        <span className={styles.groupQuantity}>
          ({totalSelected}/{property.maxSelectedOptions})
        </span>
        <Button.Icon>
          <ChevronDownIcon />
        </Button.Icon>
      </Button>
      {isOpen ? (
        <div className={styles.groupContent}>
          {property.options.map((ingredient) => (
            <IngredientElement
              key={ingredient.itemCode}
              name={ingredient.name}
              price={ingredient.unitPrice}
              quantity={value[ingredient.itemCode] ?? 0}
              disabled={!ingredient.available}
              disabledDecrement={v2_isSingleChoiceProperty(property)}
              disabledIncrement={
                groupFull && !v2_isSingleChoiceProperty(property)
              }
              max={
                v2_isSingleChoiceProperty(property)
                  ? 1
                  : ingredient.maxSelections ?? undefined
              }
              onChange={(value) =>
                onSetIngredientValue(ingredient.itemCode, value)
              }
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

export interface ConfigurableProductProps {
  readonly product: v2_ProductDetail;
  readonly configurationValues: ReturnType<
    typeof v2_useProduct
  >['configurationValues'];
  setSingleChoiceProperty(propertyId: string, option: string | null): void;
  setMultipleChoiceProperty(
    propertyId: string,
    option: string,
    quantity: number,
  ): void;
}

const ConfigurableProduct: React.FC<
  React.PropsWithChildren<ConfigurableProductProps>
> = ({
  children,
  product,
  configurationValues,
  setMultipleChoiceProperty,
  setSingleChoiceProperty,
}) => {
  const { t } = useTranslation();

  const [openProperty, setOpenProperty] = useState<string | null>(
    product.configuration.find((p) => !p.hidden)?.id ?? null,
  );

  const toggleOpen = (propertyId: string) => {
    setOpenProperty((current) => (current === propertyId ? null : propertyId));
  };

  const configurableProperties = product.configuration.filter((p) => !p.hidden);

  const selectedOptions = configurableProperties.flatMap((p) => {
    if (v2_isSingleChoiceProperty(p)) {
      const value = v2_selectSingleChoiceConfigurationPropertyValue(
        configurationValues,
        p.id,
      );

      const option = p.options.find((o) => o.itemCode === value);

      return option ? [option] : [];
    } else {
      const values = v2_selectMultipleChoiceConfigurationPropertyValue(
        configurationValues,
        p.id,
      );

      return Object.entries(values)
        .map(([itemCode, quantity = 0]) =>
          quantity > 0 ? p.options.find((o) => o.itemCode === itemCode) : null,
        )
        .filter(isSome);
    }
  });

  return (
    <div className={styles.container}>
      <Image
        src={backgroundImage}
        role="presentation"
        alt=""
        objectFit="cover"
        layout="fill"
      />
      <div className={styles.preview}>
        <Image
          src={plateImage}
          role="presentation"
          alt=""
          objectFit="contain"
          layout="fill"
        />
        {selectedOptions.map((o) => (
          <Image
            key={o.itemCode}
            src={o.image}
            role="presentation"
            alt={o.name}
            objectFit="cover"
            layout="fill"
          />
        ))}
      </div>
      <div className={styles.controls}>
        <div className={styles.info}>
          <h2 className={styles.productName}>{product.title} </h2>
          <p className={styles.productComposition}>
            {product.description ?? ''}
          </p>
          <p className={styles.productComposition}>
            <strong>{t('product.allergens')}:&nbsp;</strong>

            {product.allergens}
          </p>
        </div>
        {configurableProperties.map((property) => {
          if (v2_isSingleChoiceProperty(property)) {
            const value = v2_selectSingleChoiceConfigurationPropertyValue(
              configurationValues,
              property.id,
            );

            return (
              <IngredientGroupElement
                key={property.id}
                property={property}
                value={value ? { [value]: 1 } : {}}
                isOpen={openProperty === property.id}
                onOpen={() => toggleOpen(property.id)}
                onSetIngredientValue={(ingredient, quantity) =>
                  setSingleChoiceProperty(
                    property.id,
                    quantity > 0 ? ingredient : null,
                  )
                }
              />
            );
          } else {
            return (
              <IngredientGroupElement
                key={property.id}
                property={property}
                value={v2_selectMultipleChoiceConfigurationPropertyValue(
                  configurationValues,
                  property.id,
                )}
                isOpen={openProperty === property.id}
                onOpen={() => toggleOpen(property.id)}
                onSetIngredientValue={(ingredient, quantity) =>
                  setMultipleChoiceProperty(property.id, ingredient, quantity)
                }
              />
            );
          }
        })}
        {children}
      </div>
    </div>
  );
};

export default ConfigurableProduct;
